import {
  assistantInstantAnswers,
  type GuestIntentRule,
  type GuestIntentLibrary,
  type InstantAnswerDisclaimers,
} from '@/config/assistant/instantAnswers';

export type InstantAnswerMatch = {
  id: string;
  locale: 'en' | 'es';
  category: GuestIntentRule['category'];
  response: string;
  badgeLabel?: string;
  requiresPremiumFallback?: boolean;
  tags?: string[];
};

const stripAccents = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();

const normalize = (value: string): string => {
  return stripAccents(value).replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
};

const tokenize = (value: string): string[] => {
  if (!value) {
    return [];
  }
  return normalize(value)
    .split(' ')
    .filter(Boolean);
};

const applyDisclaimers = (
  text: string,
  locale: 'en' | 'es',
  disclaimers: InstantAnswerDisclaimers,
): string => {
  return text.replace(/{{\s*([a-z0-9_-]+)\s*}}/gi, (_match, key: string) => {
    const record = disclaimers[key];
    if (!record) {
      return '';
    }
    return record[locale] ?? record.en ?? '';
  });
};

const computeScore = (questionTokens: string[], phrases: string[]): number => {
  if (phrases.length === 0 || questionTokens.length === 0) {
    return 0;
  }
  let score = 0;
  for (const phrase of phrases) {
    const tokens = tokenize(phrase);
    if (tokens.length === 0) {
      continue;
    }
    const matched = tokens.every((token) => questionTokens.includes(token));
    if (matched) {
      score += tokens.length;
    }
  }
  return score;
};

type MatchOptions = {
  rules?: GuestIntentRule[];
  minScore?: number;
  library?: GuestIntentLibrary;
};

export const matchGuestIntent = (
  rawQuestion: string,
  locale: 'en' | 'es',
  options: MatchOptions = {},
): InstantAnswerMatch | null => {
  const question = normalize(rawQuestion);
  if (!question) {
    return null;
  }
  const questionTokens = tokenize(question);
  const library = options.library ?? assistantInstantAnswers;
  const rules = options.rules ?? library.rules;
  const minScore = options.minScore ?? 1;

  let best: { score: number; rule: GuestIntentRule } | null = null;

  for (const rule of rules) {
    const localeCopy = rule.locales[locale] ?? rule.locales.en;
    if (!localeCopy) {
      continue;
    }
    const score = computeScore(questionTokens, localeCopy.phrases);
    if (score < minScore) {
      continue;
    }
    if (!best || score > best.score || (score === best.score && rule.priority > best.rule.priority)) {
      best = { score, rule };
    }
  }

  if (!best) {
    return null;
  }

  const localeCopy = best.rule.locales[locale] ?? best.rule.locales.en;
  if (!localeCopy) {
    return null;
  }

  const response = applyDisclaimers(localeCopy.response, locale, library.disclaimers).trim();

  return {
    id: best.rule.id,
    locale,
    category: best.rule.category,
    response,
    badgeLabel: localeCopy.badgeLabel,
    requiresPremiumFallback: best.rule.requiresPremiumFallback ?? false,
    tags: best.rule.tags,
  };
};

export const getInstantAnswerLibrary = () => assistantInstantAnswers;
